import { useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import React, { createContext, FC } from 'react'
import { getJournalSectionsVariables } from '../lib/gql'
import { JOURNAL_ITEMS, JOURNAL_SECTIONS } from '../queries/journalQuery'
import { SortOrder } from '../__generated__/globalTypes'
import {
  journalItems,
  journalItemsVariables,
  journalItems_elements_edges_node,
} from '../__generated__/journalItems'
import {
  journalSections,
  journalSectionsVariables,
  journalSections_sections_nodes,
} from '../__generated__/journalSections'

interface IJournalSection {
  items: journalItems_elements_edges_node[]
  sections: journalSections_sections_nodes[]
  sectionName: string
  loadMore: () => Promise<void>
  loading: boolean
  hasNextPage: boolean
}

export const JournalSectionContext = createContext({} as IJournalSection)

const JournalSectionProvider: FC<{ section?: string; today?: string }> = ({
  section,
  today,
  children,
}) => {
  const { query } = useRouter()
  const { data: seсtionsData } = useQuery<
    journalSections,
    journalSectionsVariables
  >(JOURNAL_SECTIONS, { variables: getJournalSectionsVariables() })

  const {
    data: feed,
    fetchMore,
    loading,
  } = useQuery<journalItems, journalItemsVariables>(JOURNAL_ITEMS, {
    variables: {
      where: {
        iblock: { code: { equals: 'articles' } },
        sections: { some: { code: { equals: section } } },
        active: { equals: true },
        OR: [
          {
            activeFrom: { lte: today },
            activeTo: { equals: null },
          },
          {
            activeFrom: { lte: today },
            activeTo: { gte: today },
          },
          {
            activeFrom: { equals: null },
            activeTo: { equals: null },
          },
          {
            activeFrom: { equals: null },
            activeTo: { gte: today },
          },
        ],
      },
      first: 6,
      order: { activeFrom: SortOrder.desc },
    },
    notifyOnNetworkStatusChange: true,
  })

  const hasNextPage = feed?.elements?.pageInfo?.hasNextPage
  const cursor = feed?.elements?.pageInfo?.endCursor

  const items = feed?.elements?.edges?.map((edge) => edge.node)
  const sections = seсtionsData?.sections?.nodes?.filter(
    (section) => section.elements?.length > 0,
  )
  const sectionName = sections?.find(
    (section) => section.code === query.section,
  )?.name

  const loadMore = async () => {
    if (!hasNextPage || !cursor) {
      return null
    }

    await fetchMore({
      variables: {
        where: {
          iblock: { code: { equals: 'articles' } },
          sections: { some: { code: { equals: section } } },
          active: { equals: true },
          OR: [
            {
              activeFrom: { lte: today },
              activeTo: { equals: null },
            },
            {
              activeFrom: { lte: today },
              activeTo: { gte: today },
            },
            {
              activeFrom: { equals: null },
              activeTo: { equals: null },
            },
            {
              activeFrom: { equals: null },
              activeTo: { gte: today },
            },
          ],
        },
        first: 6,
        order: { activeFrom: SortOrder.desc },
        after: cursor,
      },
    })
  }
  return (
    <JournalSectionContext.Provider
      value={{ items, sections, sectionName, loadMore, loading, hasNextPage }}
    >
      {children}
    </JournalSectionContext.Provider>
  )
}
export default JournalSectionProvider
